import {
  compose, flip, equals, prop,
} from 'ramda';
import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { createSelector } from 'reselect';
import { useNavigate } from 'react-router-dom';
import {
  MessagesList, tryUntilResolve, rejectClientErrors, getTexts, getFont,
} from '@twnel/web-components';
import {
  encodeId, decodeId, splitConversationId, CONVERSATION_TYPE,
} from '@twnel/utils-js/web';
import { getSelectedCompanyId } from '@twnel/companies-login';
import {
  loadContacts, loadContactData, updateUnreadCounts, refreshMessages,
  loadMessagesPage, loadMessagesRangePage, markAgentReadStamp,
} from 'src/data/actions';
import {
  getConversationsById, getTopMessage, getLastMessage, getMessagesInfo,
} from 'src/data/selectors';
import { parseMessage, conversationAvatar, tagsToId } from 'src/data/util';
import { getTemplates, dateRangeLimits, isDateRangeActive } from '../selectors';

const { AGENT, GROUP } = CONVERSATION_TYPE;

export const useOpenConversation = ({ path }) => {
  const navigate = useNavigate();
  return ({ id }) => () => navigate(`/${path}/${encodeId(id)}`, { replace: true });
};

export const useConversationRenderModifier = () => {
  const selectedCompanyId = useSelector(getSelectedCompanyId);
  const conversationsSelector = React.useMemo(
    () => getConversationsById(selectedCompanyId),
    [selectedCompanyId],
  );
  const conversations = useSelector(conversationsSelector);
  return (render) => (props) => {
    const conversation = conversations[decodeId(props?.id)];
    return conversation ? render({ ...props, conversation }) : null;
  };
};

const useLoopedAction = ({ action, payload, timeout = 0 }) => {
  const dispatch = useDispatch();
  const [loadingId, setLoadingId] = React.useState(payload || null);
  React.useEffect(() => {
    if (!action || !payload) {
      setLoadingId(null);
      return undefined;
    }

    setLoadingId(payload);
    let continueLoop = true;
    const timeoutRef = setTimeout(async () => {
      await dispatch(tryUntilResolve(action(payload), compose(
        (tryAgain) => tryAgain && continueLoop,
        rejectClientErrors,
      )));
      if (continueLoop) {
        setLoadingId((currentPayload) => (
          currentPayload === payload ? null : currentPayload
        ));
      }
    }, timeout);
    return () => {
      continueLoop = false;
      clearTimeout(timeoutRef);
    };
  }, [dispatch, action, payload, timeout]);
  return !!loadingId;
};

export const useLoadContactData = ({ conversation, timeout = 0 }) => {
  let conversationId;
  if (conversation?.type === AGENT) {
    conversationId = splitConversationId(conversation).id;
  } else if (conversation?.type === GROUP) {
    conversationId = conversation.company;
  } else {
    conversationId = conversation?.id;
  }
  const loading = useLoopedAction({
    action: loadContactData,
    payload: conversationId,
    timeout,
  });
  return loading;
};

export const useRefreshMessages = ({ conversation }) => {
  const conversationId = conversation?.id;
  const infoSelector = React.useMemo(
    () => getMessagesInfo(getSelectedCompanyId, conversationId),
    [conversationId],
  );
  const info = useSelector(infoSelector);
  const shouldRefresh = info.nextPageMarker && !info.upToDate;
  const loading = useLoopedAction({
    action: shouldRefresh ? refreshMessages : null,
    payload: conversationId,
  });
  return shouldRefresh && loading;
};

export const useMarkReadStamp = ({ conversation, markReadStamp = true }) => {
  const dispatch = useDispatch();
  const lastMessageSelector = React.useMemo(
    () => getLastMessage(getSelectedCompanyId, conversation.id),
    [conversation.id],
  );
  const lastMessageId = useSelector(lastMessageSelector)?.id;
  React.useEffect(() => {
    if (markReadStamp) {
      dispatch(markAgentReadStamp(conversation.id));
    }
    dispatch(updateUnreadCounts({ conversationId: conversation.id }));
  }, [dispatch, markReadStamp, conversation.id, lastMessageId]);
};

export const useLoadMessages = (conversationId) => {
  const [topLoadedMessageId, setTopLoadedMessageId] = React.useState(null);
  React.useEffect(() => {
    setTopLoadedMessageId(null);
  }, [conversationId]);

  const topMessageSelector = React.useMemo(
    () => getTopMessage(getSelectedCompanyId, conversationId),
    [conversationId],
  );
  const topMessage = useSelector(topMessageSelector);
  const { id: topMessageId } = topMessage || {};
  const loading = topLoadedMessageId === topMessageId;

  const dispatch = useDispatch();
  const loadMessages = React.useCallback(async () => {
    setTopLoadedMessageId(topMessageId);
    await dispatch(loadMessagesPage(conversationId));
  }, [dispatch, conversationId, topMessageId]);

  // MessagesList (react-virtuoso) pide más páginas al llegar al inicio de la lista, pero con la
  // lista vacía nunca lo hace: la primera página se pide aquí, una sola vez por conversación.
  const infoSelector = React.useMemo(
    () => getMessagesInfo(getSelectedCompanyId, conversationId),
    [conversationId],
  );
  const { nextPageMarker, complete } = useSelector(infoSelector, shallowEqual);
  const needsFirstPage = !nextPageMarker && !complete;
  const firstPageRequested = React.useRef(null);
  React.useEffect(() => {
    if (needsFirstPage && firstPageRequested.current !== conversationId) {
      firstPageRequested.current = conversationId;
      loadMessages();
    }
  }, [needsFirstPage, conversationId, loadMessages]);

  return { loading, loadMessages };
};

const NO_MESSAGES = [];
const emptyRange = (key) => ({
  key,
  messages: NO_MESSAGES,
  complete: false,
  nextPageMarker: undefined,
  loading: false,
  tried: false,
});

// Mensajes de un rango de fechas pedidos directamente al backend, página a página desde el final
// del rango, sin pasar por la paginación normal de la conversación (que no cambia). Sin rango,
// no hace nada.
export const useDateRangeLoader = ({ conversationId, dateRange }) => {
  const dispatch = useDispatch();
  const active = isDateRangeActive(dateRange);
  const { start, end } = dateRangeLimits(dateRange);
  const key = active ? `${conversationId}|${start}|${end}` : null;

  const [range, setRange] = React.useState(() => emptyRange(key));
  const current = range.key === key ? range : emptyRange(key);
  if (range.key !== key) {
    setRange(current);
  }

  const loadPage = React.useCallback(async (pageMarker) => {
    setRange((state) => (state.key === key ? { ...state, loading: true } : state));
    try {
      const page = await dispatch(loadMessagesRangePage({
        conversationId, start, end, pageMarker,
      }));
      setRange((state) => (state.key === key ? {
        key,
        messages: [...page.messages, ...state.messages],
        complete: page.complete,
        nextPageMarker: page.nextPageMarker,
        loading: false,
        tried: true,
      } : state));
    } catch (error) {
      // Al subir se vuelve a intentar.
      setRange((state) => (state.key === key ? { ...state, loading: false, tried: true } : state));
    }
  }, [dispatch, conversationId, key, start, end]);

  React.useEffect(() => {
    if (key) {
      loadPage(undefined);
    }
  }, [key, loadPage]);

  const { complete, loading, nextPageMarker } = current;
  const loadMessages = React.useCallback(() => {
    if (key && !complete && !loading) {
      loadPage(nextPageMarker);
    }
  }, [key, complete, loading, nextPageMarker, loadPage]);

  const pending = loading || !current.tried;
  return React.useMemo(() => ({
    active,
    complete,
    // Sin fecha inicial, el inicio del rango es el inicio de la conversación.
    reachedBeginning: active && complete && start === -Infinity,
    messages: current.messages,
    loading: active && pending,
    loadMessages,
  }), [active, complete, start, current.messages, pending, loadMessages]);
};

// Props de MessagesList: las normales o, con un rango de fechas, las del rango.
export const useDateRangeListProps = ({
  rangeLoader, messages, complete, loading, loadMessages,
}) => {
  const texts = useSelector(getTexts);
  return React.useMemo(() => {
    if (!rangeLoader.active) {
      return {
        messages, complete, loading, loadMessages,
      };
    }
    const empty = rangeLoader.complete && !rangeLoader.loading && messages.length === 0;
    return {
      messages: empty ? [{
        id: 'date_range_empty',
        type: 'info',
        content: texts('There are no messages in this date range'),
        date: Date.now(),
      }] : messages,
      complete: rangeLoader.reachedBeginning,
      loading: rangeLoader.loading,
      loadMessages: rangeLoader.loadMessages,
    };
  }, [rangeLoader, messages, complete, loading, loadMessages, texts]);
};

export const useParsedMessages = ({
  width, messages, openInfo, resendMessage, deleteMessage, showDetails,
}) => {
  // Memorizado sobre el estado: devuelve el mismo parser mientras el estado no cambie.
  const parserSelector = React.useMemo(
    () => createSelector([(state) => state], flip(parseMessage(showDetails))),
    [showDetails],
  );
  const messageParser = useSelector(parserSelector);

  const font = useSelector(getFont);
  const { messages: parsedMessages } = React.useMemo(() => MessagesList.renderProps({
    width,
    messages: messages.map((message) => {
      const parsedMessage = messageParser(message);
      if (openInfo && (!parsedMessage.avatarType || parsedMessage.avatarType === 'agent')) {
        parsedMessage.onAuthorClick = () => openInfo(message);
      }
      if (resendMessage && deleteMessage && !parsedMessage.sent) {
        parsedMessage.onResendClick = (resend) => (
          resend ? resendMessage(message) : deleteMessage(message)
        );
      }
      return parsedMessage;
    }),
    font,
  }), [width, messages, messageParser, font, openInfo, resendMessage, deleteMessage]);
  return parsedMessages;
};

export const useGetTemplateKey = (conversation) => {
  const conversationAvatarSelector = React.useMemo(
    () => conversationAvatar(conversation),
    [conversation],
  );
  const { name } = useSelector(conversationAvatarSelector, shallowEqual);
  return React.useCallback((key = '') => {
    switch (key.toLocaleLowerCase()) {
      case 'name':
        return name.split(/\s/).shift();
      case 'fullname':
        return name;
      default:
        return null;
    }
  }, [name]);
};

export const useMessageBarProps = ({ id, sendMessage, actions }) => {
  const [showLocation, setShowLocation] = React.useState(false);
  React.useEffect(() => {
    setShowLocation(false);
  }, [id]);

  const messageBarActions = React.useMemo(
    () => actions?.map(({
      key, label, suboptions, submitOnClick,
    }) => {
      const result = [key, label];
      if (Array.isArray(suboptions)) {
        result.push({ suboptions, submitOnClick: false });
      } else if (submitOnClick !== undefined) {
        result.push({ submitOnClick });
      }
      return result;
    }) ?? [],
    [actions],
  );

  const rawTemplates = useSelector(getTemplates);
  const templates = React.useMemo(() => Object.keys(rawTemplates).reduce((list, key) => {
    if (rawTemplates[key]?.text) {
      return [...list, [key, rawTemplates[key].text]];
    }
    return list;
  }, []), [rawTemplates]);

  let type;
  let title;
  let placeholder;
  const texts = useSelector(getTexts);
  if (showLocation) {
    type = 'location';
    title = texts('Pick a location on the map');
  } else {
    placeholder = texts('Your message here...');
  }

  const onSubmit = React.useCallback(({
    content, file, location, button,
  }) => {
    if (/^\/.+/.test(content)) {
      const firstSpace = /\s/.exec(content)?.index ?? content.length;
      const command = content.slice(1, firstSpace);
      const action = actions?.find(compose(equals(command), prop('key')))?.action;
      if (action) {
        const argument = content.slice(firstSpace + 1);
        action(argument || undefined);
        return;
      }
    }

    if (location) {
      setShowLocation(false);
      sendMessage({ text: content, location });
      return;
    }

    if (content || file) {
      sendMessage({ text: content, file });
      return;
    }

    if (button === 'location') {
      setShowLocation(true);
      return;
    }

    if (button === 'cancel' && showLocation) {
      setShowLocation(false);
    }
  }, [sendMessage, actions, showLocation]);

  return {
    type,
    title,
    placeholder,
    messageBarActions,
    templates,
    onSubmit,
  };
};

export const useTagsReach = (tags = []) => {
  const tagsRef = React.useRef([]);
  const [reach, setReach] = React.useState({});

  const newId = tags?.length ? tagsToId(tags) : undefined;
  if (reach.id !== newId) {
    tagsRef.current = tags;
  }

  const dispatch = useDispatch();
  React.useEffect(() => {
    let mounted = true;
    setReach({ id: newId });
    dispatch(loadContacts({
      tags: tagsRef.current,
    })).then(({ total }) => {
      if (!mounted) {
        return;
      }
      setReach((currentReach) => {
        if (currentReach.id === newId) {
          return { ...currentReach, count: total };
        }
        return currentReach;
      });
    });
    return () => {
      mounted = false;
    };
  }, [dispatch, newId]);
  return reach.count ?? null;
};
